/**
 * New console — capabilities and roles. The one place permissions are decided.
 *
 * Every screen and control is gated on a *capability* — `roleCan(role, 'users.remove')`
 * — and never on a role's name. So giving a new role some access, or moving a permission
 * from one role to another, is a change to the two tables in this file and to nothing
 * else: no screen has to be found and edited, and no screen can quietly keep its own
 * opinion.
 *
 * Two tables:
 *
 *  - CAPABILITIES — everything the console can gate on, with what each one unlocks and
 *    where it belongs. The Roles screen and the profile's "What you can do" both render
 *    from this list, so a capability cannot be described in one place and enforced in
 *    another; the three copies of these descriptions that used to exist drifted.
 *
 *  - ROLES — who holds what. `code` is the string the API sends as `user.role`, and it
 *    is compared lower-cased.
 *
 * To add a role: one entry in ROLES. The API also has to be able to issue it
 * (`master_role`, and the users form's role list) before anyone can hold it — the
 * Roles screen shows the roles here beside the ones the API says it hands out, so that
 * gap is visible rather than hidden. The prototype sketches Front Desk and Analyst;
 * they are not here for exactly that reason.
 *
 * To add a capability: one entry in CAPABILITIES, then name it where it is enforced.
 * A superuser holds every capability except those marked `clinicOnly`, so it need not
 * be added to that role by hand.
 *
 * `isSuperAdmin` is still exported, for *scope*: is this a portfolio login that picks
 * a clinic, or a login that is one clinic? That is a different question from what the
 * account may do, and the screens that ask it (which clinic's keys, which clinic's
 * workspace) are asking about scope, not permission.
 *
 * What each role holds mirrors the classic console's route list (constants/menu.js)
 * and its in-page role checks, so the two consoles agree about what an admin can see.
 */
import { roles } from '@constants/menu';

export const CAPABILITIES = [
  /* ---- Leads ------------------------------------------------------------------- */
  { id: 'leads.view',      area: 'Leads', what: 'Open the leads list and the dashboards' },
  { id: 'leads.triage',    area: 'Leads', what: 'Record the outcome of a lead' },
  { id: 'leads.export',    area: 'Leads', what: 'Download the filtered list' },
  { id: 'leads.recording', area: 'Leads', what: 'Play call recordings and read transcripts' },
  { id: 'leads.audit',     area: 'Leads', what: 'See who recorded each lead’s outcome' },
  { id: 'leads.callbacks', area: 'Leads', what: 'Raw missed-call counts and the callback tracking table' },
  { id: 'leads.delete',    area: 'Leads', what: 'Delete a lead, and read the archive of deleted ones' },
  { id: 'patients.view',   area: 'Leads', what: 'Open the patient directory and a person’s record' },

  /* ---- Portfolio --------------------------------------------------------------- */
  { id: 'clinics.view',    area: 'Portfolio', what: 'See every clinic, and choose one from any screen' },
  { id: 'clinics.manage',  area: 'Portfolio', what: 'Add a clinic, and switch one on or off' },
  { id: 'growth.view',     area: 'Portfolio', what: 'Traffic, diagnosis and Google Ads' },
  { id: 'notify.log',      area: 'Portfolio', what: 'Read the account-wide alert log' },

  /* ---- My clinic --------------------------------------------------------------- */
  { id: 'clinic.settings',  area: 'My clinic', what: 'Edit the clinic’s own details' },
  /* `clinicOnly` like the templates: who hears about a clinic's patients is the clinic's
     own list, and the API (IsAdminPermission) refuses a portfolio login. */
  { id: 'notify.recipients', area: 'My clinic', what: 'Choose who at this clinic is emailed about leads', clinicOnly: true },
  /* `clinicOnly`: a clinic's messages to its own patients, in its own voice. The classic
     console gates every template route on the clinic admin alone; administering an
     account is not reading its correspondence, so a superuser does not get these. */
  { id: 'templates.view',   area: 'My clinic', what: 'Read message templates', clinicOnly: true },
  { id: 'templates.manage', area: 'My clinic', what: 'Edit message templates', clinicOnly: true },

  /* ---- Administration ---------------------------------------------------------- */
  { id: 'users.view',          area: 'Administration', what: 'See the user list' },
  { id: 'users.manage',        area: 'Administration', what: 'Add, edit, activate and deactivate users' },
  { id: 'users.remove',        area: 'Administration', what: 'Remove a user permanently' },
  { id: 'roles.manage',        area: 'Administration', what: 'Open the roles screen' },
  { id: 'integrations.view',   area: 'Administration', what: 'See the keys a clinic holds' },
  { id: 'integrations.manage', area: 'Administration', what: 'Change those keys' },
  { id: 'settings.manage',     area: 'Administration', what: 'Script phrases and portal-wide settings' },
  /* Superuser only while the suggestions are being watched: each press spends the AI
     key, and the prompt is still being tuned against what the staff then answer. */
  { id: 'ai.suggest',          area: 'Administration', what: 'Ask the AI for a triage suggestion on an untriaged call' }
];

export const ROLES = [
  {
    code: roles.SUPER_ADMIN,
    label: 'Super Admin',
    scope: 'Every clinic in the account',
    caps: CAPABILITIES.filter((c) => !c.clinicOnly).map((c) => c.id)
  },
  {
    code: roles.CLINIC_ADMIN,
    label: 'Admin',
    scope: 'One clinic',
    /* One clinic's own work. Other clinics, the portfolio analysis, API keys, the alert
       log, who-recorded-what and permanent removal of a user are not theirs — absent
       here, so the screens and buttons are absent too, rather than merely hidden. */
    caps: [
      'leads.view', 'leads.triage', 'leads.export', 'leads.recording', 'patients.view',
      'users.view', 'users.manage',
      'clinic.settings', 'notify.recipients', 'templates.view', 'templates.manage'
    ]
  }
];

const BY_CODE = ROLES.reduce((m, r) => Object.assign(m, { [r.code]: r }), {});

/** The role record for an API role string, or null for one this console does not know. */
export const roleFor = (role) => BY_CODE[String(role || '').toLowerCase()] || null;

export const capsFor = (role) => {
  const r = roleFor(role);
  return r ? r.caps : [];
};

/** True when this role holds the capability. A null capability means "everyone". */
export const roleCan = (role, cap) => !cap || capsFor(role).indexOf(cap) >= 0;

/** Scope, not permission — see the header. */
export const isSuperAdmin = (role) => String(role || '').toLowerCase() === roles.SUPER_ADMIN;

/** One capability's record; an unknown id gets its own id as its description. */
export const capability = (id) => CAPABILITIES.find((c) => c.id === id) || { id, area: '', what: id };

/* `pending: true` on a catalogue entry means its screen is written but its route is
   parked, so the capability is held without anything to spend it on. The profile and the
   Roles screen show such a one as held-but-empty rather than as a plain tick, which would
   send somebody looking for a screen that is not there. Nothing is parked at present. */
export const isPendingCap = (cap) => !!capability(cap).pending;

/** The catalogue grouped by area, in catalogue order — the shape the Roles screen draws. */
export const CAPABILITY_AREAS = CAPABILITIES.reduce((out, c) => {
  const last = out[out.length - 1];
  if (last && last.area === c.area) last.caps.push(c);
  else out.push({ area: c.area, caps: [c] });
  return out;
}, []);
